/**
 * SenseiMarkLoader
 *
 * A loading variant of the Sensei mark that bursts fast then decelerates
 * into a slow drift — repeating on a 9s loop.
 *
 * Timing breakdown (9s total):
 *   0 → 2s  : fast burst (~598° = ~1.66 rotations — double speed)
 *   2s → 9s : slow drift (remaining rotation over 7s)
 *
 * The outer ring spins forward, inner ring counter-rotates at a slightly
 * different rate (×0.75) to keep the original mark feel.
 */

interface SenseiMarkLoaderProps {
  size?: number;
}

const CYCLE = 9; // seconds per loop
const OUTER_DEG = 360; // outer completes exactly 1 full turn per cycle
const INNER_DEG = -360; // inner completes 1 full turn in reverse per cycle

// At t=2s (22.2% of cycle), the fast phase ends.
// Doubling the burst: 2× the original 83% → 166% of one rotation in 2s ≈ 598°
const BURST_PCT = (2 / CYCLE) * 100; // ≈ 22.22%
const OUTER_BURST = OUTER_DEG * 1.66; // ≈ 598° (nearly 2 full rotations)
const INNER_BURST = INNER_DEG * 1.66; // ≈ -598°

const styles = `
  @keyframes senseiLoaderOuter {
    0%                    { transform: rotate(0deg); }
    ${BURST_PCT.toFixed(3)}% { transform: rotate(${OUTER_BURST.toFixed(1)}deg); }
    100%                  { transform: rotate(${OUTER_DEG}deg); }
  }
  @keyframes senseiLoaderInner {
    0%                    { transform: rotate(0deg); }
    ${BURST_PCT.toFixed(3)}% { transform: rotate(${INNER_BURST.toFixed(1)}deg); }
    100%                  { transform: rotate(${INNER_DEG}deg); }
  }

  .sensei-loader-outer {
    transform-origin: 50px 50px;
    animation: senseiLoaderOuter ${CYCLE}s cubic-bezier(0.25, 0.46, 0.45, 0.94) infinite;
  }
  .sensei-loader-inner {
    transform-origin: 50px 50px;
    animation: senseiLoaderInner ${CYCLE}s cubic-bezier(0.25, 0.46, 0.45, 0.94) infinite;
  }
`;

export function SenseiMarkLoader({ size = 40 }: SenseiMarkLoaderProps) {
  return (
    <div
      style={{
        display:        "flex",
        alignItems:     "center",
        justifyContent: "center",
        width:          "100%",
        height:         "100%",
        minHeight:      size,
      }}
    >
      <style>{styles}</style>
      <svg
        width={size}
        height={size}
        viewBox="0 0 100 100"
        aria-label="Loading…"
        role="img"
      >
        <g className="sensei-loader-outer">
          <path fill="#5B6CFF" d="M 24.189 13.138 A 45 45 0 0 1 75.811 13.138 L 67.781 24.606 A 31 31 0 0 0 32.219 24.606 Z"/>
          <path fill="#5B6CFF" d="M 94.829 46.078 A 45 45 0 0 1 69.018 90.784 L 63.101 78.096 A 31 31 0 0 0 80.882 47.298 Z"/>
          <path fill="#5B6CFF" d="M 30.982 90.784 A 45 45 0 0 1 5.171 46.078 L 19.118 47.298 A 31 31 0 0 0 36.899 78.096 Z"/>
        </g>
        <g className="sensei-loader-inner">
          <path fill="#EC4899" d="M 58.893 25.568 A 26 26 0 0 1 75.605 54.515 L 63.787 52.431 A 14 14 0 0 0 54.788 36.844 Z"/>
          <path fill="#EC4899" d="M 66.712 69.917 A 26 26 0 0 1 33.288 69.917 L 41.001 60.725 A 14 14 0 0 0 58.999 60.725 Z"/>
          <path fill="#EC4899" d="M 24.395 54.515 A 26 26 0 0 1 41.107 25.568 L 45.212 36.844 A 14 14 0 0 0 36.213 52.431 Z"/>
        </g>
      </svg>
    </div>
  );
}
